import type { RepositoryIntelligenceAnalysis } from '@ashniva/types';
import { Badge, Button } from '@ashniva/ui';

import { RISK, SEVERITY, evidenceLabel, linesLabel } from '../ri-text';
import { useWorkspace } from '../workspace-context';

/**
 * What an analysis found (P14-07): the risk, the findings with their evidence, the files it affects, the tests to run
 * first and what is untested. Every word of a finding is the rule's own fixed text, shown as plain text.
 */
export function AnalysisReport({ result }: { result: RepositoryIntelligenceAnalysis }) {
  const { openCode } = useWorkspace();
  const { risk, findings, affected, recommendedTests, untestedChanges, notEvaluated, notes } =
    result;
  return (
    <div className="ri-stack">
      {risk ? (
        <section aria-label="Risk" className="ri-stack">
          <div className="ri-row">
            <Badge tone={RISK[risk.level].tone}>Risk: {RISK[risk.level].label}</Badge>
            <span>{risk.score} of 100</span>
          </div>
          {risk.drivers.length > 0 ? (
            <p className="ri-muted">Mostly from: {risk.drivers.join(', ').replace(/_/g, ' ')}</p>
          ) : null}
          <p className="ri-muted">
            A way to rank changes by what they touch, not a prediction that something breaks.
          </p>
        </section>
      ) : null}

      <section aria-label="Findings">
        <h4>Findings ({findings.length})</h4>
        {findings.length === 0 ? (
          <p className="ri-muted">The rules found nothing to point out.</p>
        ) : null}
        <ol className="ri-list">
          {findings.map((finding) => (
            <li key={finding.ordinal} className="ri-list__item">
              <div className="ri-row">
                <Badge tone={SEVERITY[finding.severity].tone}>
                  {SEVERITY[finding.severity].label}
                </Badge>
                <strong>{finding.title}</strong>
              </div>
              <p>{finding.detail}</p>
              <div className="ri-row ri-muted">
                <span className="ri-mono">{finding.ruleId}</span>
                <span>{evidenceLabel(finding.evidence.status)}</span>
                <span>confidence {finding.confidence}</span>
                {finding.symbol ? <span className="ri-mono">{finding.symbol}</span> : null}
              </div>
              {finding.path ? (
                <div>
                  <Button
                    size="sm"
                    onClick={() =>
                      openCode({
                        path: finding.path ?? '',
                        start: finding.startLine ?? 1,
                        end: finding.endLine ?? finding.startLine ?? 1,
                      })
                    }
                  >
                    {finding.path}
                    {finding.startLine
                      ? `, ${linesLabel(finding.startLine, finding.endLine ?? finding.startLine)}`
                      : ''}
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ol>
      </section>

      <section aria-label="Affected files">
        <h4>Affected files ({affected.files.length})</h4>
        <ul className="ri-list">
          {affected.files.map((file) => (
            <li key={file.path} className="ri-row">
              <span className="ri-mono">{file.path}</span>
              <span className="ri-muted">
                {file.distance === 0
                  ? 'changed'
                  : `${file.distance} step${file.distance === 1 ? '' : 's'} away`}
                {file.layer ? ` · ${file.layer}` : ''}
              </span>
            </li>
          ))}
        </ul>
        {affected.truncated ? <p className="ri-muted">Not every affected file is listed.</p> : null}
      </section>

      <section aria-label="Tests to run first">
        <h4>Tests to run first ({recommendedTests.length})</h4>
        {recommendedTests.length === 0 ? (
          <p className="ri-muted">No related tests were found.</p>
        ) : null}
        <ol className="ri-list">
          {recommendedTests.map((test) => (
            <li key={`${test.path}:${test.name ?? ''}`} className="ri-list__item">
              <div className="ri-row">
                <span className="ri-mono">{test.path}</span>
                {test.name ? <span>{test.name}</span> : null}
                <span className="ri-muted">priority {test.priority}</span>
                {test.runner ? <span className="ri-muted">{test.runner}</span> : null}
              </div>
              <span className="ri-muted">{test.reason}</span>
            </li>
          ))}
        </ol>
      </section>

      {untestedChanges.length > 0 ? (
        <section aria-label="Changes without a test">
          <h4>Changes without a test ({untestedChanges.length})</h4>
          <ul className="ri-list">
            {untestedChanges.map((change) => (
              <li key={`${change.path}:${change.symbol}`} className="ri-row">
                <span className="ri-mono">{change.symbol}</span>
                <span className="ri-muted">
                  {change.kind} {change.change} in {change.path}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {notEvaluated.length > 0 ? (
        <section aria-label="Rules that did not run">
          <h4>Rules that did not run</h4>
          <p className="ri-muted">Their absence from the findings says nothing about the change.</p>
          <ul className="ri-list">
            {notEvaluated.map((rule) => (
              <li key={rule.ruleId} className="ri-muted">
                <span className="ri-mono">{rule.ruleId}</span>: {rule.reason}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {notes.length > 0 ? (
        <ul className="ri-list" aria-label="Notes">
          {notes.map((note, index) => (
            <li key={index} className="ri-muted">
              {note}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
